import { SALESCHANNEL_FAMILY_FRIENDS } from '@vfde-sails/constants';
import { toggleArrayItem } from '@vfde-sails/utils';
import { IAccordionItemProperties } from '@vfde-brix/ws10/accordion';
import { getHardwareOverview } from '../../api/glados';
import { createAppSlice } from '../../app/createAppSlice';
import {
    getHashcode,
    startFnFValidation,
} from '@vfde-sails/validation';
import {
    RedplusTariff,
    RedTariff,
    SalesChannel,
    YoungTariff,
} from '@vfde-sails/constants';
import { AtLeast } from '@vfde-sails/types';
import { AbstractedHardwareCategory } from '@vfde-sails/glados-v2';
import { SerializedError } from '@reduxjs/toolkit';
import { CHUNK_INIT } from '../../app/constants';

/**
 * Data for initial state
 */
export interface AppState {
    /**
     * Current chunk
     */
    currentChunk: number;
    /**
     * Preset manufacturer
     */
    manufacturer: string | undefined;
    /**
     * Sales channel without groeba
     */
    salesChannel: SalesChannel | undefined;
    /**
     * Current subscription ID; false if not set
     */
    subscriptionId: RedTariff | YoungTariff | RedplusTariff | undefined;
    /**
     * Selected facets
     */
    selectedFacets: AtLeast<AbstractedHardwareCategory, 'label'>[];
    /**
     * Any potential Error
     */
    error: SerializedError | null;
}

/**
 * Initial state
 */
const initialState: AppState = {
    currentChunk: CHUNK_INIT,
    subscriptionId: undefined,
    salesChannel: undefined,
    manufacturer: undefined,
    selectedFacets: [],
    error: null,
};

/**
 * SetInitialStateArgs
 */
export interface SetInitialStateArgs {
    /* eslint-disable jsdoc/require-jsdoc */
    salesChannel: AppState['salesChannel'];
    subscriptionId: AppState['subscriptionId'];
    manufacturer: AppState['manufacturer'];
    facetLabels: string[];
    currentChunk: number;
    /* eslint-enable jsdoc/require-jsdoc */
}

const appSlice = createAppSlice({
    name: 'DeviceOverview',
    initialState,
    reducers: create => {
        const createAThunk = create.asyncThunk.withTypes<{
            rejectValue: string;
        }>();

        return {
            initApp: create.reducer(state => state),
            setInitialState: createAThunk<SetInitialStateArgs, SetInitialStateArgs>(
                async (arg, thunkApi) => {
                    if (arg.salesChannel === SALESCHANNEL_FAMILY_FRIENDS) {
                        const hashcode = getHashcode();
                        const resultAction = await thunkApi.dispatch(startFnFValidation(hashcode));
                        const validationHasFailed = startFnFValidation.rejected.match(resultAction);

                        if (validationHasFailed) {
                            return thunkApi.rejectWithValue(resultAction.payload!);
                        }
                    }

                    return arg;
                },
                {
                    fulfilled: (state, action) => {
                        state.salesChannel = action.payload.salesChannel;
                        state.subscriptionId = action.payload.subscriptionId;
                        state.manufacturer = action.payload.manufacturer;
                        state.selectedFacets = action.payload.facetLabels.map(facetLabel => ({
                            label: facetLabel,
                        }));
                        state.currentChunk = action.payload.currentChunk;
                    },
                },
            ),
            toggleFilter: create.reducer<AbstractedHardwareCategory>(
                (state, action) => {
                    state.selectedFacets = toggleArrayItem<AbstractedHardwareCategory>(
                        state.selectedFacets as AbstractedHardwareCategory[],
                        action.payload,
                        category => category.label === action.payload.label,
                    );

                    // reset chunk on filtering
                    state.currentChunk = CHUNK_INIT;
                },
            ),
            nextChunk: create.reducer<void>(
                state => {
                    state.currentChunk++;
                },
            ),
            selectDevice: create.preparedReducer(
                (productId: string, productUrl: string) => ({
                    payload: {
                        productId,
                        productUrl,
                    },
                }),
                state => state,
            ),
            toggleAccordion: create.preparedReducer(
                (itemProperties: Pick<IAccordionItemProperties, 'optOpen' | 'stdHeadline'>) => ({
                    payload: {
                        optOpen: itemProperties.optOpen,
                        stdHeadline: itemProperties.stdHeadline,
                    },
                }),
                state => state,
            ),
        };
    },
    selectors: {
        selectManufacturer: sliceState => sliceState.manufacturer,
        selectSubscriptionId: sliceState => sliceState.subscriptionId,
        selectSalesChannel: sliceState => sliceState.salesChannel,
        selectError: sliceState => sliceState.error,
        selectHasError: sliceState => !!sliceState.error,
        selectSelectedFacets: sliceState => sliceState.selectedFacets,
        selectCurrentChunk: sliceState => sliceState.currentChunk,
    },
    extraReducers: builder => {
        builder
            .addMatcher(
                getHardwareOverview.matchRejected,
                (state, action) => {
                    state.error = action.error;
                },
            )
        ;
    },
});

export default appSlice;

export const {
    initApp,
    setInitialState,
    toggleFilter,
    nextChunk,
    selectDevice,
    toggleAccordion,
} = appSlice.actions;

export const {
    selectManufacturer,
    selectSubscriptionId,
    selectSalesChannel,
    selectError,
    selectHasError,
    selectSelectedFacets,
    selectCurrentChunk,
} = appSlice.selectors;
